/**
 * AutocompleteSuggestions Component
 *
 * Dropdown UI for autocomplete suggestions.
 */

import { useRef, useEffect } from 'react';
import { stripHtml } from '../../lib/utils';
import { useCopy } from '../../lib/copy';

/**
 * Autocomplete suggestions dropdown.
 *
 * @param {Object} props
 * @param {Array} props.suggestions - List of suggestions
 * @param {number} props.selectedIndex - Currently selected index (-1 for none)
 * @param {boolean} props.isLoading - Loading state
 * @param {function} props.onSelect - Called when suggestion is selected
 * @param {function} props.onClose - Called when dropdown should close
 * @param {function} props.onMouseActivity - Called on mouse interaction to reset auto-hide timer
 * @param {number} props.timing - API response time in ms (optional)
 */
export default function AutocompleteSuggestions({
  suggestions,
  selectedIndex,
  isLoading,
  onSelect,
  onClose,
  onMouseActivity,
  timing,
}) {
  const t = useCopy();
  const listRef = useRef(null);
  const containerRef = useRef(null);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const selectedEl = listRef.current.children[selectedIndex];
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Click-outside-to-dismiss handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Use mousedown to catch clicks before they bubble
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  if (suggestions.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden"
      data-testid="suggestions"
      onMouseMove={onMouseActivity}
    >
      {/* Header */}
      <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">{t('Suggested answers')}</span>
        {timing !== null && <span className="text-[10px] text-slate-400">{timing}ms</span>}
      </div>

      {/* Loading state */}
      {isLoading && suggestions.length === 0 && (
        <div className="px-4 py-3 text-sm text-slate-500 flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin" />
          {t('Searching...')}
        </div>
      )}

      {/* Suggestions list - split into tier 1 (high confidence) and tier 2 (lower confidence) */}
      <ul ref={listRef} className="max-h-64 overflow-y-auto">
        {(() => {
          const tier1 = suggestions.filter((s) => s.tier === 1 || !s.tier);
          const tier2 = suggestions.filter((s) => s.tier === 2);
          let globalIndex = 0;

          return (
            <>
              {/* Tier 1: High confidence suggestions */}
              {tier1.map((suggestion) => {
                const index = globalIndex++;
                return (
                  <li
                    key={suggestion.answer_id}
                    data-testid={`suggestion-${index}`}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      index === selectedIndex
                        ? 'bg-indigo-50 border-l-2 border-indigo-500'
                        : 'hover:bg-slate-50 border-l-2 border-transparent'
                    }`}
                    onClick={() => onSelect(suggestion)}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-0.5">✓</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {suggestion.title}
                        </p>
                        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                          {stripHtml(suggestion.summary)}
                        </p>
                      </div>
                      <span className="text-[10px] text-slate-400 shrink-0">
                        {Math.round(suggestion.similarity * 100)}%
                      </span>
                    </div>
                  </li>
                );
              })}

              {/* Tier 2 divider and lower confidence suggestions */}
              {tier2.length > 0 && (
                <>
                  <li className="px-4 py-2 bg-slate-50 border-y border-slate-100">
                    <span className="text-xs text-slate-400 italic">These may help...</span>
                  </li>
                  {tier2.map((suggestion) => {
                    const index = globalIndex++;
                    return (
                      <li
                        key={suggestion.answer_id}
                        data-testid={`suggestion-${index}`}
                        className={`px-4 py-3 cursor-pointer transition-colors ${
                          index === selectedIndex
                            ? 'bg-amber-50 border-l-2 border-amber-400'
                            : 'hover:bg-slate-50 border-l-2 border-transparent'
                        }`}
                        onClick={() => onSelect(suggestion)}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-amber-500 mt-0.5">?</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {suggestion.title}
                            </p>
                            <p className="text-xs text-slate-400 line-clamp-2 mt-0.5">
                              {stripHtml(suggestion.summary)}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {Math.round(suggestion.similarity * 100)}%
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </>
              )}
            </>
          );
        })()}
      </ul>

      {/* Footer - dismiss suggestions */}
      <div
        className="px-4 py-2 border-t border-slate-100 text-xs text-slate-400 cursor-pointer hover:text-slate-600 hover:bg-slate-50 transition-colors flex items-center justify-between"
        onClick={onClose}
      >
        <span>{t("Not what you're looking for?")}</span>
        <span className="text-slate-300">{t('Esc to dismiss')}</span>
      </div>
    </div>
  );
}
